import * as XLSX from "xlsx-js-style";

function getNestedValue(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function normalizeExportRows(payload) {
  if (typeof payload === "string") {
    const workbook = XLSX.read(payload, { type: "string" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    return XLSX.utils.sheet_to_json(worksheet, { defval: "" });
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload && payload.data)) {
    return payload.data;
  }

  return [];
}

function sanitizeSheetName(name, usedNames) {
  const base =
    String(name || "Sheet")
      .replace(/[[\]\\/?*:]/g, "-")
      .trim() || "Sheet";

  let candidate = base.slice(0, 31);
  let counter = 1;

  while (usedNames.has(candidate)) {
    const suffix = ` (${counter})`;
    candidate = `${base.slice(0, Math.max(0, 31 - suffix.length))}${suffix}`;
    counter += 1;
  }

  usedNames.add(candidate);
  return candidate;
}

// The enrollee's public verify page, the address their QR code opens (same host as the printed QR codes).
const VERIFY_BASE_URL = "https://app.claimify.ng/verify-user";

function verifyLink(row) {
  if (!row || row.id == null || row.id === "") return "";
  const dependant = row.type === "Dependent" || (row.user_id != null && row.account_type !== "Principal");
  return `${VERIFY_BASE_URL}/${dependant ? "dependant" : "client"}/${row.id}`;
}

// Name of the enrolment officer: the row's own (principal or dependant), else a dependant's principal's.
function enrolledByName(row) {
  const officer = row.userenrolledby || getNestedValue(row, "user.userenrolledby");
  if (!officer) return "";
  return officer.full_name || [officer.lastname, officer.firstname].filter(Boolean).join(" ");
}

function mapRowForExport(row, meta) {
  const out = {};
  const excludedFields = new Set([
    "full_name",
    "Full Name",
    // "sector",
    // "Program",
    "user_image",
    "image_url",
    "image",
    "Image",
  ]);

  const setIfEmpty = (key, value) => {
    if (out[key] === undefined) {
      out[key] = value == null ? "" : value;
    }
  };

  const hmoName =
    getNestedValue(row, "usertpa.organization_name") ||
    getNestedValue(row, "user.usertpa.organization_name") ||
    row.org_id;

  const providerName =
    getNestedValue(row, "userprovider.agency_name") ||
    getNestedValue(row, "user.userprovider.agency_name") ||
    row.provider_id ||
    row.provider;

  const sectorValue = row.sector;
  const placeOfWork = row.place_of_work || getNestedValue(row, "user.place_of_work");
  // Also resolve from backend-preformatted friendly keys
  const hmoNameFriendly = row["HMO"] || hmoName;
  const providerNameFriendly = row["Health Provider"] || providerName;
  const placeOfWorkFriendly = row["Place of Work"] || placeOfWork;

  Object.keys(row).forEach((key) => {
    const value = row[key];

    if (excludedFields.has(key)) {
      return;
    }

    // Skip nested relationship objects/arrays as columns.
    if (value && typeof value === "object") {
      return;
    }

    switch (key) {
      case "relationShipType":
        setIfEmpty("Relationship", value);
        break;
      case "Relationship":
        setIfEmpty("Relationship", value);
        break;
      case "org_id":
        setIfEmpty("HMO", hmoNameFriendly);
        break;
      case "provider_id":
      case "provider":
        setIfEmpty("Health Provider", providerNameFriendly);
        break;
      case "place_of_work":
        setIfEmpty("Place of Work", placeOfWorkFriendly);
        break;
      case "id_card_number":
        setIfEmpty("Enrollee ID", value);
        break;
      case "phone_number":
        setIfEmpty("Phone Number", value);
        break;
      case "dob":
        setIfEmpty("Date of Birth", value);
        break;
      case "created_at":
        setIfEmpty("Date Enrolled", value);
        break;
      case "localgovt":
        setIfEmpty("LGA", value);
        break;
      case "category_of_vulnerable_group":
        setIfEmpty("Vulnerable Group", value);
        break;
      case "firstname":
        setIfEmpty("First Name", value);
        break;
      case "lastname":
        setIfEmpty("Last Name", value);
        break;
      case "middlename":
        setIfEmpty("Middle Name", value);
        break;
      case "middle_name":
        setIfEmpty("Middle Name", value);
        break;
      case "dependants_count":
        setIfEmpty("Dependents Count", value);
        break;
      case "enrolled_by":
        // the id alone means nothing to readers: use the enrolment officer's name
        setIfEmpty("Enrolled By", enrolledByName(row) || "");
        break;
      default:
        setIfEmpty(key, value);
        break;
    }
  });

  // Ensure key fields appear with readable values when present in related models only.
  if (sectorValue != null)          setIfEmpty("Program", sectorValue);
  if (hmoNameFriendly != null)      setIfEmpty("HMO", hmoNameFriendly);
  if (providerNameFriendly != null) setIfEmpty("Health Provider", providerNameFriendly);
  if (placeOfWorkFriendly != null)  setIfEmpty("Place of Work", placeOfWorkFriendly);
  if (row["Enrolled By"] == null && (row.userenrolledby || getNestedValue(row, "user.userenrolledby"))) {
    setIfEmpty("Enrolled By", enrolledByName(row));
  }

  const agencyId = String(
    (meta && meta.filters && meta.filters["Agency ID"]) ||
    (meta && meta.agencyId) ||
    ""
  );

  if (agencyId === "930972") {
    [
      "Ward",
      "ward",
      "NIN",
      "nimc_number",
      "Age",
      "age",
      "Age Group",
      "age_group",
      "Date Enrolled",
      "created_at",
      "Community",
      "community",
      "LGA",
      "localgovt",
      "Education",
      "education",
      "Occupation",
      "occupation",
      "Address",
      "address1",
      "Salary Number",
      "salary_number",
      "Vulnerable Group",
      "category_of_vulnerable_group",
      "Means of Identification",
      "Means of ID",
      "means_of_id",
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        delete out[key];
      }
    });
  }

  // Keep HMO as the last column across all sheets, then the QR code link.
  if (Object.prototype.hasOwnProperty.call(out, "HMO")) {
    const hmoValue = out["HMO"];
    delete out["HMO"];
    out["HMO"] = hmoValue;
  }
  const qrLink = out["QR Code Link"] || verifyLink(row);
  delete out["QR Code Link"];
  out["QR Code Link"] = qrLink;

  // Keep the same leading columns for grouped and ungrouped sheets.
  const preferredOrder = [
    "Enrollee ID",
    "Relationship",
    "Program",
    "Last Name",
    "First Name",
    "Middle Name",
  ];

  const orderedOut = {};
  preferredOrder.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(out, key)) {
      orderedOut[key] = out[key];
    }
  });

  Object.keys(out).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(orderedOut, key)) {
      orderedOut[key] = out[key];
    }
  });

  return orderedOut;
}

// ─── Styling helpers ─────────────────────────────────────────────────────────

const DARK_BLUE = "1F4E79";
const WHITE     = "FFFFFF";

function applyBoldHeader(worksheet) {
  if (!worksheet || !worksheet["!ref"]) return;
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let C = range.s.c; C <= range.e.c; C++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!worksheet[addr]) continue;
    worksheet[addr].s = {
      font:      { bold: true, color: { rgb: WHITE }, sz: 11 },
      fill:      { patternType: "solid", fgColor: { rgb: DARK_BLUE } },
      alignment: { horizontal: "center", wrapText: true },
    };
  }
}

function styleSectionCell(ws, rowIdx) {
  const addr = XLSX.utils.encode_cell({ r: rowIdx, c: 0 });
  if (ws[addr]) ws[addr].s = { font: { bold: true, sz: 12, color: { rgb: DARK_BLUE } } };
}

function styleTableHeader(ws, rowIdx, numCols) {
  for (let C = 0; C < numCols; C++) {
    const addr = XLSX.utils.encode_cell({ r: rowIdx, c: C });
    if (ws[addr]) ws[addr].s = {
      font:      { bold: true, color: { rgb: WHITE }, sz: 11 },
      fill:      { patternType: "solid", fgColor: { rgb: DARK_BLUE } },
      alignment: { horizontal: "center" },
    };
  }
}

function styleTotalRow(ws, rowIdx, numCols) {
  for (let C = 0; C < numCols; C++) {
    const addr = XLSX.utils.encode_cell({ r: rowIdx, c: C });
    if (ws[addr]) ws[addr].s = { font: { bold: true } };
  }
}

// ─── Sheet builders ───────────────────────────────────────────────────────────

function buildReportInfoSheet(meta) {
  const aoa = [
    ["REPORT INFORMATION"],
    [],
    ["Generated By", meta.generatedBy || ""],
    ["Generated At", meta.generatedAt || ""],
    [],
    ["FILTERS APPLIED"],
  ];
  Object.entries(meta.filters || {}).forEach(([k, v]) => {
    aoa.push([k, String(v || "All")]);
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  styleSectionCell(ws, 0);
  styleSectionCell(ws, 5);
  ws["!cols"] = [{ wch: 25 }, { wch: 55 }];
  return ws;
}

function buildSummarySheetWithMeta(meta, groupLabel, summaryEntries, totalCount) {
  const aoa = [];
  const sectionRows = [];

  aoa.push(["REPORT INFORMATION"]);  sectionRows.push(0);
  aoa.push([]);
  aoa.push(["Generated By", meta.generatedBy || ""]);
  aoa.push(["Generated At", meta.generatedAt || ""]);
  aoa.push([]);
  aoa.push(["FILTERS APPLIED"]);     sectionRows.push(5);

  Object.entries(meta.filters || {}).forEach(([k, v]) => {
    aoa.push([k, String(v || "All")]);
  });

  aoa.push([]);
  sectionRows.push(aoa.length);
  aoa.push(["SUMMARY"]);

  const tableHeaderRow = aoa.length;
  aoa.push([groupLabel, "Total"]);
  summaryEntries.forEach(({ label, count }) => aoa.push([String(label), count]));

  const totalRow = aoa.length;
  aoa.push(["TOTAL", totalCount]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  sectionRows.forEach((r) => styleSectionCell(ws, r));
  styleTableHeader(ws, tableHeaderRow, 2);
  styleTotalRow(ws, totalRow, 2);
  ws["!cols"] = [{ wch: 42 }, { wch: 12 }];
  return ws;
}

// ─── Workbook builder ─────────────────────────────────────────────────────────

function buildExportWorkbook(rows, groupBy, meta) {
  const workbook = XLSX.utils.book_new();
  const usedNames = new Set();
  const formattedRows = rows.map((row) => mapRowForExport(row, meta));
  const headers = formattedRows.length ? Object.keys(formattedRows[0]) : [];

  if (!formattedRows.length) {
    const worksheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enrollees");
    return workbook;
  }

  if (groupBy === "provider" || groupBy === "hmo" || groupBy === "place_of_work") {
    const groupResolvers = {
      hmo: (row) => ({
        key:   row["HMO"] || getNestedValue(row, "usertpa.organization_name") || getNestedValue(row, "user.usertpa.organization_name") || row.org_id || "Unknown HMO",
        label: row["HMO"] || getNestedValue(row, "usertpa.organization_name") || getNestedValue(row, "user.usertpa.organization_name") || row.org_id || "Unknown HMO",
      }),
      provider: (row) => ({
        key:   row["Health Provider"] || getNestedValue(row, "userprovider.agency_name") || getNestedValue(row, "user.userprovider.agency_name") || row.provider_id || row.provider || "Unknown Provider",
        label: row["Health Provider"] || getNestedValue(row, "userprovider.agency_name") || getNestedValue(row, "user.userprovider.agency_name") || row.provider_id || row.provider || "Unknown Provider",
      }),
      place_of_work: (row) => ({
        key:   row["Place of Work"] || row.place_of_work || getNestedValue(row, "user.place_of_work") || "Unknown Place of Work",
        label: row["Place of Work"] || row.place_of_work || getNestedValue(row, "user.place_of_work") || "Unknown Place of Work",
      }),
    };

    const grouped = {};

    formattedRows.forEach((row) => {
      const resolved = groupResolvers[groupBy](row);
      const key = String(resolved.key).trim();
      const label = String(resolved.label).trim();
      const groupKey = key || label || "Unknown";

      if (!grouped[groupKey]) {
        grouped[groupKey] = { label: label || groupKey, rows: [] };
      }
      grouped[groupKey].rows.push(row);
    });

    const groupedKeys = Object.keys(grouped);
    const sortedGroupKeys = groupBy === "provider"
      ? groupedKeys.sort((a, b) => String(grouped[a].label).localeCompare(String(grouped[b].label), undefined, { sensitivity: "base" }))
      : groupedKeys;

    const summaryEntries = sortedGroupKeys.map((key) => ({
      label: grouped[key].label,
      count: grouped[key].rows.length,
    }));

    const groupLabel = groupBy === "hmo" ? "HMO" : groupBy === "provider" ? "Provider" : "Place of Work";
    const summarySheet = buildSummarySheetWithMeta(meta || {}, groupLabel, summaryEntries, formattedRows.length);
    XLSX.utils.book_append_sheet(workbook, summarySheet, sanitizeSheetName("Summary", usedNames));

    sortedGroupKeys.forEach((key) => {
      const sheetName = sanitizeSheetName(grouped[key].label, usedNames);
      const worksheet = XLSX.utils.json_to_sheet(grouped[key].rows, {
        header: headers,
      });
      applyBoldHeader(worksheet);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    return workbook;
  }

  // Ungrouped: Report Info sheet + data sheet
  if (meta) {
    XLSX.utils.book_append_sheet(workbook, buildReportInfoSheet(meta), sanitizeSheetName("Report Info", usedNames));
  }

  const worksheet = XLSX.utils.json_to_sheet(formattedRows, { header: headers });
  applyBoldHeader(worksheet);
  XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName("Enrollees", usedNames));
  return workbook;
}

export function downloadGroupedXlsx(payload, groupBy, fileName, meta) {
  const exportRows = normalizeExportRows(payload);
  const workbook = buildExportWorkbook(exportRows, groupBy, meta);
  XLSX.writeFile(workbook, fileName);
}

export function downloadEnrolleesCsv(rows, fileName, meta) {
  const mapped = rows.map((row) => mapRowForExport(row, meta));
  const headers = [];
  const seen = new Set();
  mapped.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        headers.push(key);
      }
    });
  });

  const escapeCell = (value) => {
    const text = value == null ? "" : String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const lines = [headers.map(escapeCell).join(",")];
  mapped.forEach((row) => {
    lines.push(headers.map((key) => escapeCell(row[key])).join(","));
  });

  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
